/**
 * Shared model for Tier 1 immunity explainers.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { GeneratorCode, NeutralizationModeDisplayName } from "@csk/core";
import type { ExplainerCrossLink, ExplainerIdentity } from "../shared/index.js";
import { PRIMARY, SAFE } from "../../design-system/tokens.js";
import type { MODE_COLOR } from "../../design-system/tokens.js";
import tierOneData from "./tier-one-immunity.data.json" with { type: "json" };

export const CATEGORICAL_IMMUNITY_ACCENT = PRIMARY[700];
export const PURITY_ACCENT = SAFE[700];

export interface TierOneGenerator {
  readonly id: GeneratorCode;
  readonly name: string;
  readonly principle: string;
}

export interface TierOneConsequence {
  readonly code: string;
  readonly name: string;
  readonly generator: GeneratorCode;
  readonly mode: NeutralizationModeDisplayName;
  readonly structure: string;
}

export interface PurityConstructor {
  readonly name: string;
  readonly lane: string;
  readonly meaning: string;
}

interface TierOneImmunityData {
  readonly generators: readonly TierOneGenerator[];
  readonly consequences: readonly TierOneConsequence[];
  readonly purity: {
    readonly constructors: readonly PurityConstructor[];
    readonly excludedCandidate: PurityConstructor;
  };
}

export const DATA = tierOneData as TierOneImmunityData;

export const GENERATOR_Y: Readonly<Record<GeneratorCode, number>> = {
  G1: 58,
  G2: 130,
  G3: 202,
  G4: 274,
};

export const MODE_TO_TOKEN: Readonly<
  Record<NeutralizationModeDisplayName, keyof typeof MODE_COLOR>
> = {
  IMMUNE: "immune",
  INERT: "inert",
  CONTAINED: "contained",
};

export const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Ten NT consequences", href: "#explainer-data-origin", accent: PRIMARY[700] },
  { label: "Purity Invariant pruning", href: "#explainer-purity", accent: PURITY_ACCENT },
];

export const PURITY_CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "Categorical Immunity Theorem cone",
    href: "#explainer-categorical-immunity",
    accent: CATEGORICAL_IMMUNITY_ACCENT,
  },
  { label: "S1 Neutralization Theorems", href: "#explainer-data-origin", accent: PRIMARY[700] },
];

export const CATEGORICAL_IMMUNITY_IDENTITY: ExplainerIdentity = {
  id: "explainer-categorical-immunity",
  kind: "Master theorem · Tier 1: The Immunity",
  title: "Categorical Immunity Theorem",
  subtitle: "Tier 1 master theorem",
  accent: CATEGORICAL_IMMUNITY_ACCENT,
};

export const PURITY_IDENTITY: ExplainerIdentity = {
  id: "explainer-purity",
  kind: "Master theorem · Tier 1: The Immunity",
  title: "Purity Invariant",
  subtitle: "Code PURITY",
  accent: PURITY_ACCENT,
};
